// js/reports.js — Reports tab: summary table + charts + CSV/JSON export

import * as Cal  from './calendar.js';
import * as Data from './data.js';
import { exportCSV, exportJSON } from './export.js';
import { buildNavBar } from './nav.js';

// Custom date range state — persists across re-renders within the session
let _customFrom = '';
let _customTo   = '';
let _searchQuery   = '';
let _searchDebounce = null;

function filterEntries(entries, query, projects, categories) {
  const q = query.trim().toLowerCase();
  if (!q) return entries;
  const projName = id => (projects.find(p => p.id === id)?.name || '').toLowerCase();
  const catName  = id => (categories.find(c => c.id === id)?.name || '').toLowerCase();
  return entries.filter(e =>
    (e.description || '').toLowerCase().includes(q) ||
    projName(e.projectId).includes(q) ||
    catName(e.categoryId).includes(q)
  );
}

function renderSearchBar(c) {
  const wrap = document.createElement('div');
  wrap.className = 'flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border';
  wrap.style.cssText = 'background:#fff;border-color:#E4E3E0;';

  const icon = document.createElement('span');
  icon.textContent = '🔍';
  icon.style.cssText = 'font-size:0.9rem;flex-shrink:0;color:#7889B3;';

  const input = document.createElement('input');
  input.type = 'text';
  input.id = 'rpt-search';
  input.className = 'cell-input';
  input.style.cssText = 'flex:1;border:none;box-shadow:none;padding:2px 0;background:transparent;';
  input.placeholder = 'Search by description, project or category…';
  input.value = _searchQuery;

  const clearBtn = document.createElement('button');
  clearBtn.textContent = '✕';
  clearBtn.title = 'Clear search (Escape)';
  clearBtn.style.cssText = `display:${_searchQuery ? 'inline' : 'none'};color:#7889B3;font-size:0.8rem;padding:0 2px;`;

  const clear = () => {
    _searchQuery = '';
    clearTimeout(_searchDebounce);
    renderReports();
  };

  clearBtn.addEventListener('click', clear);

  input.addEventListener('input', () => {
    clearTimeout(_searchDebounce);
    _searchDebounce = setTimeout(() => {
      _searchQuery = input.value;
      renderReports();
    }, 200);
  });

  input.addEventListener('keydown', e => {
    if (e.key === 'Escape') clear();
  });

  wrap.appendChild(icon);
  wrap.appendChild(input);
  wrap.appendChild(clearBtn);
  c.appendChild(wrap);
}

function renderMatchingEntries(c, filteredEntries, projects, categories) {
  const section = document.createElement('div');
  section.className = 'mt-6';

  const heading = document.createElement('p');
  heading.className = 'text-xs font-semibold uppercase tracking-wider mb-3';
  heading.style.color = '#7889B3';
  const n = filteredEntries.length;
  heading.textContent = `${n} matching entr${n === 1 ? 'y' : 'ies'}`;
  section.appendChild(heading);

  const sorted = [...filteredEntries].sort((a, b) => b.date.localeCompare(a.date));
  const formatH = h => h ? `${Math.round(h * 10) / 10}h` : '—';

  const table = document.createElement('table');
  table.className = 'w-full text-sm border-collapse';
  table.innerHTML = `
    <thead>
      <tr class="border-b-2 border-gray-300 text-left text-xs text-gray-500 uppercase tracking-wider">
        <th class="py-2 px-3 font-medium">Date</th>
        <th class="py-2 px-3 font-medium">Category</th>
        <th class="py-2 px-3 font-medium">Project</th>
        <th class="py-2 px-3 font-medium">Start</th>
        <th class="py-2 px-3 font-medium">End</th>
        <th class="py-2 px-3 font-medium">Duration</th>
        <th class="py-2 px-3 font-medium">Description</th>
      </tr>
    </thead>
    <tbody></tbody>
  `;

  const tbody = table.querySelector('tbody');

  sorted.forEach(e => {
    const proj = projects.find(p => p.id === e.projectId);
    const cat  = categories.find(c => c.id === e.categoryId);
    const d = new Date(e.date + 'T00:00:00');
    const dateStr = isNaN(d.getTime()) ? (e.date || '—') : d.toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
    const tr = document.createElement('tr');
    tr.className = 'border-b border-gray-100 hover:bg-gray-50';
    tr.innerHTML = `
      <td class="py-2 px-3 text-gray-500 whitespace-nowrap">${dateStr}</td>
      <td class="py-2 px-3 whitespace-nowrap">
        <span class="inline-flex items-center gap-1.5">
          <span style="width:8px;height:8px;border-radius:50%;background:${cat?.color || '#94a3b8'};flex-shrink:0;display:inline-block;"></span>
          ${cat?.name || '—'}
        </span>
      </td>
      <td class="py-2 px-3 text-gray-600">${proj?.name || '—'}</td>
      <td class="py-2 px-3 text-gray-600 whitespace-nowrap">${e.startTime || '—'}</td>
      <td class="py-2 px-3 text-gray-600 whitespace-nowrap">${e.endTime || '—'}</td>
      <td class="py-2 px-3 text-gray-600 whitespace-nowrap">${formatH(e.duration)}</td>
      <td class="py-2 px-3 text-gray-500"></td>
    `;
    tr.lastElementChild.textContent = e.description || '';
    tbody.appendChild(tr);
  });

  section.appendChild(table);
  c.appendChild(section);
}

export async function renderReports() {
  const c = document.getElementById('view-reports');
  c.innerHTML = '';

  // Nav bar (shares calendar state with Timesheet)
  const navWrap = document.createElement('div');
  buildNavBar(navWrap, renderReports);
  c.appendChild(navWrap);

  // ── Custom date range bar ──────────────────────────────────────────────────
  const rangeBar = document.createElement('div');
  rangeBar.className = 'flex items-center gap-2 flex-wrap mb-4 p-3 rounded-lg border';
  rangeBar.style.cssText = 'background:#F4F8FF;border-color:#A8DDFF;';
  rangeBar.innerHTML = `
    <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Custom range</span>
    <input type="date" id="rpt-from" class="cell-input text-sm" style="width:150px;flex:none" value="${_customFrom}" />
    <span class="text-xs text-gray-400">to</span>
    <input type="date" id="rpt-to"   class="cell-input text-sm" style="width:150px;flex:none" value="${_customTo}" />
    <button id="rpt-apply" class="text-sm px-3 py-1.5 rounded text-white" style="background:#364A7A">Apply</button>
    ${_customFrom ? `<button id="rpt-clear" class="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100">✕ Clear</button>
    <span class="text-xs font-medium ml-1" style="color:#364A7A">Showing ${_customFrom} → ${_customTo}</span>` : ''}
  `;
  c.appendChild(rangeBar);

  // ── Search bar ──────────────────────────────────────────────────────────────
  renderSearchBar(c);

  rangeBar.querySelector('#rpt-apply').addEventListener('click', () => {
    const f = rangeBar.querySelector('#rpt-from').value;
    const t = rangeBar.querySelector('#rpt-to').value;
    if (!f || !t) return;
    if (f > t) { alert('From date must be before To date.'); return; }
    _customFrom = f;
    _customTo   = t;
    renderReports();
  });
  rangeBar.querySelector('#rpt-clear')?.addEventListener('click', () => {
    _customFrom = '';
    _customTo   = '';
    renderReports();
  });

  const calRange = Cal.getCurrentRange();
  const from = _customFrom || calRange.from;
  const to   = _customTo   || calRange.to;
  const [entries, projects, categories] = await Promise.all([
    Data.getEntries(from, to),
    Data.getProjects(),
    Data.getCategories(),
  ]);
  const filteredEntries = filterEntries(entries, _searchQuery, projects, categories);

  // Export buttons
  const exportRow = document.createElement('div');
  exportRow.className = 'flex gap-2 mb-4';
  exportRow.innerHTML = `
    <button id="btn-csv"  class="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700">Download CSV</button>
    <button id="btn-json" class="px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700">Download JSON</button>
  `;
  exportRow.querySelector('#btn-csv').addEventListener('click',  async () => exportCSV(entries));
  exportRow.querySelector('#btn-json').addEventListener('click', async () => exportJSON(entries));
  c.appendChild(exportRow);

  if (!entries.length) {
    const msg = document.createElement('p');
    msg.className = 'text-gray-400 text-sm';
    msg.textContent = 'No entries in this period.';
    c.appendChild(msg);
    return;
  }

  // Charts
  await renderCharts(c, entries, categories, from, to);

  if (_searchQuery && !filteredEntries.length) {
    const noMatch = document.createElement('p');
    noMatch.className = 'text-sm mt-2';
    noMatch.style.color = '#7889B3';
    noMatch.textContent = 'No entries match your search.';
    c.appendChild(noMatch);
    return;
  }

  // Build summary: rows = active projects + "No Project", cols = categories used in period
  const activeProjects = projects.filter(p => p.active);
  const catIds = [...new Set(filteredEntries.map(e => e.categoryId))];
  const usedCats = categories.filter(cat => catIds.includes(cat.id));

  // Accumulate hours[projectId][categoryId]
  const hours = {};
  const addHours = (pid, cid, h) => {
    if (!hours[pid]) hours[pid] = {};
    hours[pid][cid] = (hours[pid][cid] || 0) + h;
  };
  filteredEntries.forEach(e => addHours(e.projectId || '__none__', e.categoryId, e.duration || 0));

  const table = document.createElement('table');
  table.className = 'w-full text-sm border-collapse';

  // Header
  const projectHeader = _searchQuery
    ? 'Project <span style="color:#d97706;font-size:0.7rem;font-weight:normal;margin-left:4px">● filtered</span>'
    : 'Project';
  const headerCols = [projectHeader, ...usedCats.map(c => c.name), 'Total'];
  table.innerHTML = `
    <thead>
      <tr class="border-b-2 border-gray-300 text-left text-xs text-gray-500 uppercase tracking-wider">
        ${headerCols.map(h => `<th class="py-2 px-3 font-medium">${h}</th>`).join('')}
      </tr>
    </thead>
    <tbody></tbody>
    <tfoot></tfoot>
  `;

  const tbody = table.querySelector('tbody');
  const tfoot = table.querySelector('tfoot');

  const formatH = h => h ? `${Math.round(h * 10) / 10}h` : '—';

  // Project rows — only show rows that have hours in this period
  const allProjectIds = [...activeProjects.map(p => p.id), '__none__'];
  const colTotals = {};

  allProjectIds.forEach(pid => {
    const projHours = hours[pid];
    if (!projHours) return;
    const rowTotal = Object.values(projHours).reduce((s, h) => s + h, 0);
    const proj = projects.find(p => p.id === pid);
    const projName = pid === '__none__' ? '(No project)' : (proj?.name || pid);
    const isBillable = pid !== '__none__' && !!proj?.billable;
    const tr = document.createElement('tr');
    tr.className = 'border-b border-gray-100 hover:bg-gray-50';
    tr.innerHTML = `
      <td class="py-2 px-3 font-medium">
        ${projName}${isBillable ? ' <span class="text-xs text-blue-400 font-normal">billable</span>' : ''}
      </td>
      ${usedCats.map(cat => {
        const h = projHours[cat.id] || 0;
        colTotals[cat.id] = (colTotals[cat.id] || 0) + h;
        return `<td class="py-2 px-3 text-gray-600">${formatH(h)}</td>`;
      }).join('')}
      <td class="py-2 px-3 font-semibold">${formatH(rowTotal)}</td>
    `;
    tbody.appendChild(tr);
  });

  // Grand total footer
  const grandTotal = Object.values(colTotals).reduce((s, h) => s + h, 0);
  const tfRow = document.createElement('tr');
  tfRow.className = 'border-t-2 border-gray-300 bg-gray-50 font-semibold';
  tfRow.innerHTML = `
    <td class="py-2 px-3">Total</td>
    ${usedCats.map(cat => `<td class="py-2 px-3">${formatH(colTotals[cat.id] || 0)}</td>`).join('')}
    <td class="py-2 px-3">${formatH(grandTotal)}</td>
  `;
  tfoot.appendChild(tfRow);

  // Billable total footer row
  const billablePids = new Set(projects.filter(p => p.billable).map(p => p.id));
  const billCatTotals = {};
  let billTotal = 0;
  allProjectIds.forEach(pid => {
    if (!billablePids.has(pid)) return;
    const projHours = hours[pid];
    if (!projHours) return;
    usedCats.forEach(cat => {
      const h = projHours[cat.id] || 0;
      billCatTotals[cat.id] = (billCatTotals[cat.id] || 0) + h;
      billTotal += h;
    });
  });

  if (billTotal > 0) {
    const pct = grandTotal > 0 ? Math.round(billTotal / grandTotal * 100) : 0;
    const billRow = document.createElement('tr');
    billRow.className = 'border-t border-gray-200 bg-blue-50 text-xs text-blue-700';
    billRow.innerHTML = `
      <td class="py-2 px-3 font-medium">Billable (${pct}% utilisation)</td>
      ${usedCats.map(cat => `<td class="py-2 px-3">${formatH(billCatTotals[cat.id] || 0)}</td>`).join('')}
      <td class="py-2 px-3 font-semibold">${formatH(billTotal)}</td>
    `;
    tfoot.appendChild(billRow);
  }

  c.appendChild(table);

  if (_searchQuery) {
    renderMatchingEntries(c, filteredEntries, projects, categories);
  }

  // Restore focus to search input after re-render so typing is uninterrupted
  if (_searchQuery) {
    const si = c.querySelector('#rpt-search');
    if (si) { si.focus(); si.setSelectionRange(si.value.length, si.value.length); }
  }
}

// ── Shared formatter ─────────────────────────────────────────────────────────

function fmtDur(h) {
  if (!h) return '—';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm ? `${hh}h ${mm}m` : `${hh}h`;
}

// ── YTD summary card ─────────────────────────────────────────────────────────

async function buildYTDCard(year) {
  const [ytdEntries, projects] = await Promise.all([
    Data.getEntries(`${year}-01-01`, `${year}-12-31`),
    Data.getProjects(),
  ]);

  const totals = {};
  ytdEntries.forEach(e => {
    const k = e.projectId || '__none__';
    totals[k] = (totals[k] || 0) + (e.duration || 0);
  });
  const grandTotal = Object.values(totals).reduce((s, h) => s + h, 0);

  const card = document.createElement('div');
  card.className = 'card p-4 flex flex-col';

  // Header
  const hdr = document.createElement('div');
  hdr.className = 'flex items-baseline justify-between mb-3';
  hdr.innerHTML = `
    <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">${year} Year to Date</p>
    <span class="text-sm font-bold" style="color:#49B9FF">${fmtDur(grandTotal)}</span>
  `;
  card.appendChild(hdr);

  // Project rows (scrollable if tall)
  const list = document.createElement('div');
  list.style.cssText = 'overflow-y:auto;max-height:160px;flex:1;';

  Object.entries(totals)
    .sort(([, a], [, b]) => b - a)
    .forEach(([pid, hours]) => {
      const name = pid === '__none__'
        ? 'No project'
        : (projects.find(p => p.id === pid)?.name || 'Unknown');
      const pct = grandTotal > 0 ? Math.round(hours / grandTotal * 100) : 0;

      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:baseline;gap:6px;padding:4px 0;border-bottom:1px solid #F0F4FA;';
      row.innerHTML = `
        <span style="font-size:0.78rem;color:#364A7A;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${name}</span>
        <span style="font-size:0.78rem;font-weight:600;color:#49B9FF;white-space:nowrap">${fmtDur(hours)}</span>
        <span style="font-size:0.72rem;color:#7889B3;width:28px;text-align:right;flex-shrink:0">${pct}%</span>
      `;
      list.appendChild(row);
    });

  card.appendChild(list);
  return card;
}

// ── Charts ────────────────────────────────────────────────────────────────────

async function renderCharts(c, entries, categories, from, to) {
  const wrap = document.createElement('div');
  wrap.className = 'grid gap-4 mb-6';
  wrap.style.gridTemplateColumns = 'repeat(auto-fill, minmax(260px, 1fr))';

  // ── Bar chart: hours per day ──────────────────────────────────────────────
  const barCard = document.createElement('div');
  barCard.className = 'card p-4';
  barCard.innerHTML = '<p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Hours per Day</p>';
  const barCanvas = document.createElement('canvas');
  barCanvas.style.maxHeight = '200px';
  barCard.appendChild(barCanvas);

  const dayTotals = {};
  entries.forEach(e => { dayTotals[e.date] = (dayTotals[e.date] || 0) + (e.duration || 0); });

  const barLabels = [], barValues = [];
  const cur = new Date(from + 'T00:00:00');
  const end = new Date(to   + 'T00:00:00');
  while (cur <= end) {
    const iso = cur.toISOString().slice(0, 10);
    barLabels.push(cur.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
    barValues.push(Math.round((dayTotals[iso] || 0) * 10) / 10);
    cur.setDate(cur.getDate() + 1);
  }

  new Chart(barCanvas, {
    type: 'bar',
    data: {
      labels: barLabels,
      datasets: [{
        data: barValues,
        backgroundColor: '#49B9FF',
        borderRadius: 3,
        borderSkipped: false,
      }],
    },
    options: {
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.y}h` } },
      },
      scales: {
        x: {
          ticks: { maxTicksLimit: 14, font: { size: 10 } },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 10 }, callback: v => `${v}h` },
        },
      },
    },
  });

  // ── Donut chart: hours by category ───────────────────────────────────────
  const donutCard = document.createElement('div');
  donutCard.className = 'card p-4';
  donutCard.innerHTML = '<p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">By Category</p>';
  const donutCanvas = document.createElement('canvas');
  donutCanvas.style.maxHeight = '200px';
  donutCard.appendChild(donutCanvas);

  const catTotals = {};
  entries.forEach(e => { catTotals[e.categoryId] = (catTotals[e.categoryId] || 0) + (e.duration || 0); });
  const usedCats = categories.filter(cat => catTotals[cat.id]);

  new Chart(donutCanvas, {
    type: 'doughnut',
    data: {
      labels: usedCats.map(cat => cat.name),
      datasets: [{
        data: usedCats.map(cat => Math.round((catTotals[cat.id] || 0) * 10) / 10),
        backgroundColor: usedCats.map(cat => cat.color),
        borderWidth: 2,
        borderColor: '#fff',
      }],
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'right',
          labels: { font: { size: 11 }, boxWidth: 12, padding: 8 },
        },
        tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed}h` } },
      },
    },
  });

  wrap.appendChild(barCard);
  wrap.appendChild(donutCard);
  wrap.appendChild(await buildYTDCard(parseInt(from.slice(0, 4), 10)));
  c.appendChild(wrap);
}
